import React, { Suspense, lazy, useState } from "react";
import "./App.css";
import "./testConfig";
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import LinearProgress from "@material-ui/core/LinearProgress";
import { ThemeProvider } from "@material-ui/styles";
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import ForgotPasswordResetSent from "./routes/Login/ForgotPasswordResetSent";
import ResetPasswordConfirmed from "./routes/Login/ResetPasswordConfirmed";
import { AuthenticatedRoute, Logout } from "./components/Authenticate/Authenticate";
import Chrome from "./components/Chrome/Chrome";

import TagManager from 'react-gtm-module'
import initGlobalState from "./components/App/Utils";


import theme from './theme';
import LoadingRoute from "./routes/Login/LoadingRoute";

const Home = lazy(() => import("./routes/Home/Home"));
const Customize = lazy(() => import("./routes/Customize/Customize"));
const Profile = lazy(() => import("./routes/Account/Profile"));
const Users = lazy(() => import("./routes/Account/Users"));
const UsersAddEdit = lazy(() => import("./routes/Account/UsersAddEdit"));
const Contact = lazy(() => import("./routes/Contact/Contact"));
const Locations = lazy(() => import("./routes/Locations/Locations"));
const LocationsImport = lazy(() => import("./routes/Locations/LocationsImport"));
const Reporting = lazy(() => import("./routes/Reporting/Reporting"));
const LoginRoute = lazy(() => import("./routes/Login/Login"));
const SignUp = lazy(() => import("./routes/Login/SignUp"));
const ForgotPassword = lazy(() => import("./routes/Login/ForgotPassword"));
const ResetPassword = lazy(() => import("./routes/Login/ResetPassword"));
const PasswordChangeWithCode = lazy(() => import("./routes/Login/PasswordChangeWithCode"));
// const merge = require('deepmerge');

// Global state stuff
let globalState = initGlobalState();


// Google Analytics
TagManager.initialize({
    gtmId: process.env.REACT_APP_GTM_ID,
    dataLayer: {
        merchantGroupId: globalState.merchantGroup.id
    }
});


export default function App() {
    const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);

    return (
        <ThemeProvider theme={theme}>
            <Router>
                <Suspense fallback={<LinearProgress/>}>
                    <Switch>
                        <Route
                            exact
                            path="/forgot-password"
                            render={routeProps => (
                                <ForgotPassword history={routeProps.history}/>
                            )}
                        />
                        <Route
                            exact
                            path="/change-password-with-code/:email"
                            render={routeProps => (
                                <PasswordChangeWithCode history={routeProps.history}/>
                            )}
                        />
                        <Route
                            exact
                            path="/forgot-password-sent/:email"
                            render={routeProps => <ForgotPasswordResetSent/>}
                        />
                        <Route
                            exact
                            path="/reset-password/:resetToken"
                            render={routeProps => (
                                <ResetPassword
                                    match={routeProps.match}
                                    history={routeProps.history}
                                    setPasswordResetModalOpen={setPasswordResetModalOpen}
                                />
                            )}
                        />
                        <Route
                            exact
                            path="/signup/:email/:resetToken"
                            render={routeProps => (
                                <SignUp
                                    match={routeProps.match}
                                    history={routeProps.history}
                                />
                            )}
                        />
                        <Route
                            exact
                            path="/reset-password-confirmed"
                            render={routeProps => <ResetPasswordConfirmed/>}
                        />
                        <AuthenticatedRoute
                            path="/secure/account/users/edit/:id"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={UsersAddEdit}
                                />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={Home}
                                    />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/loading"
                            exact
                            component={(routeProps) => (
                                <LoadingRoute/>
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/customize"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={Customize} />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/account"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={Profile} />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/account/users"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={Users}
                                />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/account/users/add"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={UsersAddEdit}
                                />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/account/users/edit/:id"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={UsersAddEdit}
                                />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/contact"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={Contact} />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/locations"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={Locations} />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/locations/import"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={LocationsImport} />
                            )}
                        />
                        <AuthenticatedRoute
                            path="/secure/reporting"
                            exact
                            component={(routeProps) => (
                                <Chrome
                                    routeProps={routeProps}
                                    component={Reporting} />
                            )}
                        />
                        <Route
                            path="/secure/logout"
                            exact
                            render={() => {
                                Logout();
                                return <Redirect to="/" />;
                            }}
                        />
                        <Route
                            exact
                            path={["/", "/signup"]}
                            render={routeProps => (
                                <LoginRoute
                                    match={routeProps.match}
                                    history={routeProps.history}
                                />
                            )}
                        />
                        <Route
                            path="/"
                            render={routeProps => {
                                return <Redirect to="/" />
                            }}
                        />
                    </Switch>
                </Suspense>
            </Router>
            <Dialog
                aria-labelledby="password-reset-modal-title"
                open={passwordResetModalOpen}
            >
                <DialogTitle id="password-reset-modal-title">Password Reset Success!</DialogTitle>
                <DialogContent style={{ marginBottom: '30px' }}>
                    Your password has been reset.
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" variant="outlined" onClick={() => { setPasswordResetModalOpen(false); }}>Ok</Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}
